/**
 * Deep Link Service
 * Handles deep link detection and parsing for native platforms
 * Supports both custom URL schemes (com.vertiv.its://) and universal links (https://)
 */

import {
  REACT_APP_DEEP_LINK_SCHEME,
  REACT_APP_DEEP_LINK_SCHEME_IOS,
} from '@env';
import { Linking, Platform } from 'react-native';

import { validateReturnUrl } from '../utils/urlValidation';

const DEEP_LINK_SCHEME =
  Platform.OS === 'ios'
    ? REACT_APP_DEEP_LINK_SCHEME_IOS
    : REACT_APP_DEEP_LINK_SCHEME;

if (!DEEP_LINK_SCHEME) {
  console.warn(
    `⚠️ REACT_APP_DEEP_LINK_SCHEME${
      Platform.OS === 'ios' ? '_IOS' : ''
    } is not set - deep links will not work correctly!`,
  );
}

export interface DeepLinkInfo {
  url: string;
  route: string;
  params: Record<string, string>;
}

/**
 * Deep Link Service
 * Manages deep link handling for native iOS and Android platforms
 */
class DeepLinkService {
  private listeners: Array<(url: string) => void> = [];

  /**
   * The one start-up of this process, shared by every caller.
   *
   * ⚠️ `initialize()` used to subscribe to `Linking` and re-deliver
   * `getInitialURL()` on EVERY call, and App.tsx called it from an effect that
   * re-ran on each auth or request change. Each re-run added a subscription
   * nobody removed (a warm link was then handled N times) and handed the
   * cold-start URL to the listeners again, so after a cold-start link, opening
   * any other request bounced back to the linked one (#1599 Story 3.3).
   * Latching the promise makes a second call a no-op that resolves with the
   * first.
   */
  private initialization: Promise<void> | null = null;

  /**
   * Initializes the deep link listener: delivers the cold-start URL once and
   * subscribes to warm links once per process. Safe to call more than once.
   * Register listeners (`addListener`) BEFORE calling it, or the cold-start
   * URL is delivered to nobody.
   * @returns Promise<void>
   */
  initialize(): Promise<void> {
    if (Platform.OS === 'web') {
      // Web platform - deep links not supported
      return Promise.resolve();
    }
    if (!this.initialization) {
      this.initialization = this.start();
    }
    return this.initialization;
  }

  private async start(): Promise<void> {
    // Warm links first, and outside the cold-start `try`: the latch makes this
    // the only start-up, so a `getInitialURL` that throws must not also cost
    // every warm link for the life of the process. One subscription, never
    // removed.
    try {
      Linking.addEventListener('url', event => {
        this.handleDeepLink(event.url);
      });
    } catch (error) {
      console.error('❌ DeepLinkService: Failed to subscribe to links:', error);
    }

    try {
      // Handle cold start (app was not running)
      const initialUrl = await Linking.getInitialURL();
      if (initialUrl) {
        this.handleDeepLink(initialUrl);
      }
    } catch (error) {
      console.error(
        '❌ DeepLinkService: Failed to read the launch link:',
        error,
      );
    }
  }

  /**
   * Registers a callback to be called when a deep link is received
   * @param callback - Function to call with the deep link URL
   * @returns Cleanup function to remove the listener
   */
  addListener(callback: (url: string) => void): () => void {
    this.listeners.push(callback);

    // Return cleanup function
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Handles an incoming deep link URL
   * Notifies all registered listeners
   * @param url - The deep link URL to handle
   */
  private handleDeepLink(url: string): void {
    // Notify all registered listeners

    // Notify all listeners
    this.listeners.forEach(listener => {
      try {
        listener(url);
      } catch (error) {
        console.error('❌ DeepLinkService: Listener error:', error);
      }
    });
  }

  /**
   * Parses a deep link URL into route and parameters
   * Supports both custom schemes (com.vertiv.its://) and universal links (https://)
   *
   * @param url - The URL to parse
   * @returns DeepLinkInfo object with route and params, or null if parsing fails
   *
   * @example
   * parseDeepLink('com.vertiv.its://request-detail?id=123')
   * // Returns: { url: '...', route: '/request-detail', params: { id: '123' } }
   *
   * parseDeepLink('https://itsqa.vertiv.com/request-detail?id=123')
   * // Returns: { url: '...', route: '/request-detail', params: { id: '123' } }
   */
  parseDeepLink(url: string): DeepLinkInfo | null {
    try {
      let pathname = '';
      let searchParams: URLSearchParams;

      // Handle custom scheme (e.g., com.vertiv.its://request-detail?id=123)
      const schemePrefix = `${DEEP_LINK_SCHEME}://`;
      if (url.startsWith(schemePrefix)) {
        const urlWithoutScheme = url.replace(schemePrefix, '');
        const [path, query] = urlWithoutScheme.split('?');
        pathname = '/' + path; // Add leading slash for consistency
        searchParams = new URLSearchParams(query || '');
        // Parsed custom scheme URL
      }
      // Handle universal links (https://itsqa.vertiv.com/request-detail?id=123 or https://site.vertiv.com/request-detail?id=123)
      else if (url.startsWith('http://') || url.startsWith('https://')) {
        const urlObj = new URL(url);

        // Validate that this is a Vertiv domain (must match entitlements)
        const validDomains = ['itsqa.vertiv.com', 'site.vertiv.com'];
        if (!validDomains.includes(urlObj.hostname)) {
          console.warn(
            '⚠️ DeepLinkService: Invalid domain for universal link:',
            urlObj.hostname,
          );
          return null;
        }

        pathname = urlObj.pathname;
        searchParams = urlObj.searchParams;
        // Parsed universal link
      }
      // Unknown format
      else {
        // The scheme only: the rest can carry request and thread ids.
        console.warn(
          '⚠️ DeepLinkService: Unknown URL format:',
          url.split(':')[0],
        );
        return null;
      }

      // Extract query parameters
      const params: Record<string, string> = {};
      searchParams.forEach((value, key) => {
        params[key] = value;
      });

      // Construct the internal route
      const route =
        pathname +
        (Object.keys(params).length > 0 ? '?' + searchParams.toString() : '');

      // Validate the route against allowed paths
      const allowedPaths = [
        '/request-detail',
        '/create-request',
        '/view-requests',
        '/role-management',
        '/request',
        '/internal-user',
        '/request-access',
      ];

      const routePath = pathname.split('?')[0]; // Get path without query params
      if (!allowedPaths.includes(routePath)) {
        console.warn(
          '⚠️ DeepLinkService: Unauthorized path from deep link:',
          routePath,
        );
        return null;
      }

      // Additional validation using existing URL validation
      if (!validateReturnUrl(route)) {
        console.warn(
          '⚠️ DeepLinkService: Invalid route from deep link:',
          route,
        );
        return null;
      }

      return {
        url,
        route,
        params,
      };
    } catch (error) {
      console.error('❌ DeepLinkService: Failed to parse deep link:', error);
      return null;
    }
  }

  /**
   * Opens a URL (web link or deep link)
   * Useful for triggering deep links programmatically
   * @param url - The URL to open
   * @returns Promise<boolean> - true if opened successfully
   */
  async openUrl(url: string): Promise<boolean> {
    try {
      const canOpen = await Linking.canOpenURL(url);
      if (canOpen) {
        await Linking.openURL(url);
        // URL opened successfully
        return true;
      } else {
        console.warn('⚠️ DeepLinkService: Cannot open URL:', url);
        return false;
      }
    } catch (error) {
      console.error('❌ DeepLinkService: Failed to open URL:', error);
      return false;
    }
  }

  /**
   * Converts an internal route to a custom scheme deep link
   * @param route - Internal route (e.g., '/request-detail?id=123')
   * @returns Custom scheme URL (e.g., 'com.vertiv.its://request-detail?id=123')
   */
  toCustomSchemeUrl(route: string): string {
    // Remove leading slash
    const routeWithoutSlash = route.startsWith('/')
      ? route.substring(1)
      : route;
    return `${DEEP_LINK_SCHEME}://${routeWithoutSlash}`;
  }

  /**
   * Converts an internal route to a universal link
   * @param route - Internal route (e.g., '/request-detail?id=123')
   * @param domain - Your domain (e.g., 'https://yourdomain.com')
   * @returns Universal link URL
   */
  toUniversalLink(route: string, domain: string): string {
    // Ensure domain doesn't have trailing slash
    const cleanDomain = domain.endsWith('/') ? domain.slice(0, -1) : domain;
    // Ensure route has leading slash
    const cleanRoute = route.startsWith('/') ? route : '/' + route;
    return `${cleanDomain}${cleanRoute}`;
  }
}

// Export singleton instance
export const deepLinkService = new DeepLinkService();
